// Enhanced BadgeWall Component - Clubhouse 2.0 Design
// Displays user's earned badges with rarity effects, recently earned section, and detail sheet

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Target, Trophy, Flame, Star, Users, Lock, Sparkles, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { BadgeImage } from '@/components/profile/BadgeImage';

const CATEGORY_CONFIG = {
  analysis: { 
    icon: Target, 
    label: 'Analysis', 
    color: 'text-blue-500', 
    gradient: 'from-blue-500 to-cyan-500' 
  },
  practice: { 
    icon: Trophy, 
    label: 'Practice', 
    color: 'text-green-500', 
    gradient: 'from-green-500 to-emerald-500' 
  },
  streak: { 
    icon: Flame, 
    label: 'Streaks', 
    color: 'text-orange-500', 
    gradient: 'from-orange-500 to-red-500' 
  },
  milestone: { 
    icon: Star, 
    label: 'Milestones', 
    color: 'text-purple-500', 
    gradient: 'from-purple-500 to-pink-500' 
  },
  social: { 
    icon: Users, 
    label: 'Social', 
    color: 'text-pink-500', 
    gradient: 'from-pink-500 to-rose-500' 
  },
} as const;

const RARITY_CONFIG = {
  common: {
    bg: 'bg-gray-50',
    border: 'border-gray-300',
    glow: '',
    label: 'Common',
    color: 'text-gray-600',
  },
  rare: {
    bg: 'bg-blue-50',
    border: 'border-blue-400',
    glow: 'shadow-[0_0_15px_rgba(59,130,246,0.3)]',
    label: 'Rare',
    color: 'text-blue-600',
  },
  epic: {
    bg: 'bg-purple-50',
    border: 'border-purple-400',
    glow: 'shadow-[0_0_20px_rgba(168,85,247,0.4)] animate-pulse',
    label: 'Epic',
    color: 'text-purple-600',
  },
  legendary: {
    bg: 'bg-gradient-to-br from-yellow-50 via-orange-50 to-yellow-50',
    border: 'border-yellow-400',
    glow: 'shadow-[0_0_25px_rgba(251,191,36,0.5)]',
    label: 'Legendary',
    color: 'text-yellow-600',
  },
};

interface BadgeWallProps {
  userId?: string; // Optional - defaults to current user
}

export function BadgeWall({ userId }: BadgeWallProps) {
  // Archived: clubhouse APIs moved to archive/community
  // const { data: achievements, isLoading, error } = useUserAchievements();
  // const { data: allAchievements } = useAllAchievements();
  const achievements: UserAchievement[] = [];
  const isLoading = false;
  const error = null;
  const allAchievements: Achievement[] = [];
  const [selectedBadge, setSelectedBadge] = useState<UserAchievement | null>(null);
  const isMobile = useIsMobile();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Badges</CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Badges</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Failed to load badges: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!achievements || achievements.length === 0) {
    return (
      <Card className="border-2 border-dashed">
        <CardHeader>
          <CardTitle>Badges</CardTitle>
        </CardHeader>
        <CardContent className="py-12">
          <div className="text-center space-y-4">
            <div className="text-6xl mb-4">🏅</div>
            <div>
              <p className="font-medium mb-1">Start Your Journey</p>
              <p className="text-sm text-muted-foreground">
                Complete drills and engage with the clubhouse to earn badges!
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Separate unlocked and locked badges
  const unlockedBadges = achievements.filter(a => a.unlocked_at && a.achievement);
  
  // Get locked badges from all achievements
  const lockedBadges: UserAchievement[] = (allAchievements || [])
    .filter(a => !a.unlocked_at && a.progress !== undefined && a.progress < 100)
    .map(a => ({
      id: a.user_achievement_id || `locked-${a.id}`,
      user_id: '',
      achievement_id: a.id,
      unlocked_at: null,
      progress_data: { progress: a.progress || 0 },
      awarded_via: null,
      metadata: null,
      achievement: a as Achievement,
    }));

  // Get recently earned (top 3 most recent)
  const recentBadges = unlockedBadges
    .sort((a, b) => new Date(b.unlocked_at!).getTime() - new Date(a.unlocked_at!).getTime())
    .slice(0, 3);

  // Group badges by category (include both unlocked and locked)
  const allBadges = [...unlockedBadges, ...lockedBadges];
  const badgesByCategory = allBadges.reduce((acc, userAchievement) => {
    const achievement = userAchievement.achievement as Achievement;
    if (!achievement) return acc;

    const category = achievement.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(userAchievement);
    return acc;
  }, {} as Record<string, UserAchievement[]>);

  return (
    <>
      <Card className="overflow-hidden border-2">
        <CardHeader className="bg-gradient-to-r from-[#196644]/5 to-[#A7F3A1]/5 border-b pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <Sparkles className="w-5 h-5 text-[#F9C94E]" />
                Badges
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {unlockedBadges.length} earned{lockedBadges.length > 0 ? ` • ${lockedBadges.length} in progress` : ''}
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-8">
          {/* Recently Earned */}
          {recentBadges.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-[#3DC68D]" />
                <h3 className="font-semibold text-foreground">Recently Earned</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {recentBadges.map((badge, index) => (
                  <BadgeCard
                    key={badge.id}
                    badge={badge}
                    onClick={() => setSelectedBadge(badge)}
                    delay={index * 0.05}
                  />
                ))}
              </div>
            </div>
          )}

          {/* By Category */}
          {Object.entries(CATEGORY_CONFIG).map(([categoryKey, config]) => {
            const categoryBadges = badgesByCategory[categoryKey] || [];
            if (categoryBadges.length === 0) return null;
            
            const Icon = config.icon;
            const earnedCount = categoryBadges.length;

            return (
              <div key={categoryKey}>
                <div className="flex items-center gap-2 mb-4">
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${config.gradient} flex items-center justify-center`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="font-semibold text-foreground">{config.label}</h3>
                  <Badge variant="secondary" className="ml-auto">
                    {earnedCount}/{categoryBadges.length}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {categoryBadges.map((badge, index) => (
                    <BadgeCard
                      key={badge.id}
                      badge={badge}
                      onClick={() => setSelectedBadge(badge)}
                      delay={index * 0.03}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Badge Detail Sheet */}
      <Sheet open={!!selectedBadge} onOpenChange={() => setSelectedBadge(null)}>
        <SheetContent 
          side={isMobile ? "bottom" : "right"}
          className={isMobile ? "h-[80vh]" : "max-w-md"}
        >
          {selectedBadge && <BadgeDetail badge={selectedBadge} />}
        </SheetContent>
      </Sheet>
    </>
  );
}

interface BadgeCardProps {
  badge: UserAchievement;
  onClick: () => void;
  delay?: number;
}

function BadgeCard({ badge, onClick, delay = 0 }: BadgeCardProps) {
  const achievement = badge.achievement as Achievement;
  if (!achievement) return null;

  const rarity = RARITY_CONFIG[achievement.rarity];
  const isLocked = !badge.unlocked_at;
  const progress = badge.progress_data?.progress || (isLocked ? 0 : 100);

  return (
    <motion.button
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay }}
      whileHover={{ scale: 1.05, y: -4 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`
        relative p-4 rounded-2xl border-2 transition-all cursor-pointer
        ${isLocked ? 'bg-gray-100 border-gray-300 opacity-60' : `${rarity.bg} ${rarity.border} ${rarity.glow}`}
        hover:shadow-lg
      `}
    >
      {/* Legendary shimmer effect */}
      {!isLocked && achievement.rarity === 'legendary' && (
        <motion.div
          className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"
          animate={{
            x: ['-200%', '200%'],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatDelay: 3,
            ease: 'linear',
          }}
        />
      )}

      <div className="relative flex flex-col items-center gap-2">
        {/* Badge Image */}
        <div className="relative mb-1">
          {isLocked ? (
            <div className="flex items-center justify-center w-14 h-14 bg-gray-200 rounded-full">
              <Lock className="w-6 h-6 text-gray-400" />
            </div>
          ) : (
            <BadgeImage
              category={achievement.category}
              rarity={achievement.rarity}
              badge_key={achievement.badge_key}
              achievement_id={achievement.id}
              image_path={achievement.image_path}
              size={56}
            />
          )}
        </div>

        {/* Title */}
        <p className="text-xs font-semibold leading-tight text-center line-clamp-2 min-h-[2rem]">
          {achievement.title}
        </p>

        {/* Progress or Date */}
        {isLocked && badge.progress_data ? (
          <div className="w-full space-y-1">
            <Progress value={badge.progress_data.progress || 0} className="h-1.5" />
            <p className="text-[10px] text-muted-foreground text-center">
              {badge.progress_data.progress || 0}%
            </p>
          </div>
        ) : !isLocked && badge.unlocked_at ? (
          <p className="text-[10px] text-muted-foreground">
            {new Date(badge.unlocked_at).toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric' 
            })}
          </p>
        ) : null}

        {/* Rarity indicator */}
        {!isLocked && (
          <Badge 
            variant="outline" 
            className={`text-[9px] px-1.5 py-0 h-4 ${rarity.color} border-current`}
          >
            {rarity.label}
          </Badge>
        )}
      </div>
    </motion.button>
  );
}

function BadgeDetail({ badge }: { badge: UserAchievement }) {
  const achievement = badge.achievement as Achievement;
  if (!achievement) return null;

  const rarity = RARITY_CONFIG[achievement.rarity];
  const category = CATEGORY_CONFIG[achievement.category];
  const CategoryIcon = category.icon;
  const isLocked = !badge.unlocked_at;

  return (
    <div className="space-y-6">
      <SheetHeader>
        <div className="flex flex-col items-center text-center space-y-4">
          {/* Large badge icon */}
          <div className={`
            relative p-6 rounded-3xl border-2 ${rarity.bg} ${rarity.border} ${rarity.glow}
            flex items-center justify-center
          `}>
            {isLocked ? (
              <Lock className="w-16 h-16 text-gray-400 mx-auto" />
            ) : (
              <BadgeImage
                category={achievement.category}
                rarity={achievement.rarity}
                badge_key={achievement.badge_key}
                achievement_id={achievement.id}
                image_path={achievement.image_path}
                size={80}
              />
            )}
          </div>

          <div>
            <SheetTitle className="text-2xl mb-2">{achievement.title}</SheetTitle>
            <div className="flex items-center justify-center gap-2">
              <Badge variant="outline" className={rarity.color}>
                {rarity.label}
              </Badge>
              <Badge variant="secondary" className="flex items-center gap-1">
                <CategoryIcon className="w-3 h-3" />
                {category.label}
              </Badge>
            </div>
          </div>
        </div>
      </SheetHeader>

      <div className="space-y-4">
        <div>
          <h4 className="font-semibold mb-2">Description</h4>
          <SheetDescription className="text-base">
            {achievement.description}
          </SheetDescription>
        </div>

        {isLocked && badge.progress_data && (
          <div>
            <h4 className="font-semibold mb-2">Progress</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {badge.progress_data.requirement || 'In progress'}
                </span>
                <span className="font-medium">{badge.progress_data.progress || 0}%</span>
              </div>
              <Progress value={badge.progress_data.progress || 0} className="h-2" />
            </div>
          </div>
        )}

        {!isLocked && badge.unlocked_at && (
          <div>
            <h4 className="font-semibold mb-2">Unlocked</h4>
            <p className="text-muted-foreground">
              {new Date(badge.unlocked_at).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>
        )}

        {/* Share button for unlocked badges */}
        {!isLocked && (
          <div className="pt-4 border-t">
            <button className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-[#196644] to-[#2B8A67] text-white font-semibold hover:shadow-lg transition-all">
              Share Achievement 🎉
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

